import React, { useState } from 'react';
import { NoDataFoundWidget } from '@components/Widgets/layout.components';
import { AvTooltip } from '@fabric-ui/components';
import { Up as UpArrow } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { ellipsis } from '@fabric-ui/utils';
import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import useQueryObject from '../../../../hooks/useQueryObjectSql';
import { calculatePercentageChange, getLast30DaysDate } from '../../utils';
import PercentageChange from '../percentageChange';
import { getTop5AssetByIncidents, getTop5UsersByIncidents } from '../queries';
import { EntityTypes, FilterContext, SelectedWidgetData, TableDataType } from '../types';
import WidgetTooltip, { TooltipDivider } from './WidgetTooltip';

interface Top5AttackedEntitiesProps {
  selected?: SelectedWidgetData;
  onSelect?: (selected: SelectedWidgetData) => void;
  closeInteractive?: () => void;
  filterContext: FilterContext;
}

const Top5AttackedEntities = (props: Top5AttackedEntitiesProps) => {
  const { onSelect, selected, closeInteractive } = props;

  const Y_TICK_WIDTH = 220;
  const [currentSelect, setCurrentSelect] = useState<SelectedWidgetData | undefined>(selected);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const { filterContext } = props;

  const theme = useTheme();

  const { data: userData = [], isLoading: isLoadingUsers } = useQueryObject({
    queryObject: [getTop5UsersByIncidents([], filterContext)],
    options: { placeholderData: keepPreviousData },
  });

  const { data: assetData = [], isLoading: isLoadingAssets } = useQueryObject({
    queryObject: [getTop5AssetByIncidents([], filterContext)],
    options: { placeholderData: keepPreviousData },
  });

  const top5Entities = React.useMemo(() => {
    if (isLoadingUsers || isLoadingAssets) {
      return [];
    }
    const combined = [...assetData, ...userData];
    return combined.toSorted((a, b) => b.value - a.value).slice(0, 5);
  }, [assetData, userData]);

  const userKeys = React.useMemo(() => top5Entities.filter(d => d.type === EntityTypes.USER).map(d => d.entityId), [top5Entities]);
  const assetKeys = React.useMemo(() => top5Entities.filter(d => d.type === EntityTypes.ASSET).map(d => d.entityId), [top5Entities]);

  const { data: dataLast30DaysUser = [], isLoading: isLoadingLast30DaysUser } = useQueryObject({
    queryObject: [
      getTop5UsersByIncidents(userKeys, {
        ...filterContext,
        dateRange: [getLast30DaysDate(filterContext.dateRange[1]), filterContext.dateRange[1]],
      }),
    ],
    options: {
      placeholderData: keepPreviousData,
      enabled: !!userKeys.length,
    },
  });

  const { data: dataLast30DaysAsset = [], isLoading: isLoadingLast30DaysAsset } = useQueryObject({
    queryObject: [
      getTop5AssetByIncidents(assetKeys, {
        ...filterContext,
        dateRange: [getLast30DaysDate(filterContext.dateRange[1]), filterContext.dateRange[1]],
      }),
    ],
    options: {
      placeholderData: keepPreviousData,
      enabled: !!assetKeys.length,
    },
  });

  const data = React.useMemo(() => {
    if (!top5Entities.length) return [];

    const getLast30DaysValue = entity => {
      const dataSet = entity.type === EntityTypes.USER ? dataLast30DaysUser : dataLast30DaysAsset;
      const oldData = dataSet.find(d => d.entityId === entity.entityId);
      return oldData ? oldData.value : 0;
    };

    return top5Entities.map(entity => {
      const last30Days = getLast30DaysValue(entity);
      const percentage = calculatePercentageChange(last30Days, entity.value);

      return {
        ...entity,
        last30Days,
        percentage,
      };
    });
  }, [top5Entities, dataLast30DaysUser, dataLast30DaysAsset]);

  const isLoading = isLoadingUsers || isLoadingAssets || (!!top5Entities.length && (isLoadingLast30DaysUser || isLoadingLast30DaysAsset));

  const handleSelect = (label: string | undefined) => {
    if (label === undefined) return;
    const { type, entityId } = data.find(d => d.name === label);

    const key = type === EntityTypes.USER ? 'user._key' : 'asset._key';
    const selectedItem: SelectedWidgetData = { name: label, filter: { [key]: [entityId] }, tableType: TableDataType.INCIDENT };

    if (onSelect) {
      onSelect(selectedItem);
    }
    setCurrentSelect(currentSelect?.name === label ? undefined : selectedItem);
  };

  if (!currentSelect && closeInteractive) {
    closeInteractive();
  }

  return (
    <Box sx={{ ...flex.col, minHeight: 250, gap: 1, height: '100%', padding: '16px 24px' }}>
      <Typography sx={{ whiteSpace: 'nowrap', fontSize: '18px' }} variant="h2">
        Top 5 Attacked Entities
      </Typography>

      <Box sx={{ flex: 1, width: '100%' }}>
        {!isLoading ? (
          data.length === 0 ? (
            <Box sx={{ height: '100%', ...flex.row }}>
              <NoDataFoundWidget />
            </Box>
          ) : (
            <Box sx={{ ...flex.col, height: '100%' }}>
              <Box sx={{ ...flex.row, gap: '16px', color: theme.palette.colors.neutrals[500], fontSize: 12 }}>
                <Box sx={{ ...flex.itemsCenter, gap: '4px' }}>
                  <UpArrow style={{ width: '15px', height: '15px', color: theme.palette.colors.neutrals[500] }} />
                  Trend Change % last 30 days
                </Box>
                <Box sx={{ ...flex.itemsCenter, gap: '4px' }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: theme.palette.colors.primary[350] }} />
                  Incident Count
                </Box>
              </Box>
              <ResponsiveContainer debounce={100} width="100%" height="100%">
                <BarChart
                  data={data}
                  onClick={v => handleSelect(v.activeLabel)}
                  style={{ cursor: 'pointer' }}
                  layout="vertical"
                  barSize={16}
                  onMouseMove={state => {
                    if (state.isTooltipActive && state.activeCoordinate) {
                      setTooltipPosition(state.activeCoordinate);
                    } else {
                      setTooltipPosition(null);
                    }
                  }}>
                  <CartesianGrid stroke={theme.palette.colors.neutrals[300]} strokeWidth={0.5} horizontal={false} />
                  <XAxis
                    type="number"
                    tickLine={false}
                    tickCount={4}
                    style={{ fontSize: 12, color: theme.palette.colors.neutrals[500], strokeWidth: 0.5 }}
                    axisLine={{ stroke: theme.palette.colors.neutrals[300], strokeWidth: 0.5 }}
                  />

                  <YAxis
                    type="category"
                    tickLine={false}
                    onClick={v => handleSelect(v.activeLabel)}
                    tick={<CustomYAxisTick data={data} Y_TICK_WIDTH={Y_TICK_WIDTH} />}
                    width={Y_TICK_WIDTH}
                    axisLine={false}
                    dataKey="name"
                  />
                  <Tooltip
                    cursor={{ fill: theme.palette.colors.neutrals[200] }}
                    allowEscapeViewBox={{ x: true, y: true }}
                    position={tooltipPosition ? { x: tooltipPosition.x - Y_TICK_WIDTH, y: tooltipPosition.y } : undefined}
                    content={<CustomTooltip />}
                  />
                  <Bar dataKey="value">
                    {data.map(_entry => (
                      <Cell
                        fill={
                          currentSelect === undefined || _entry.name === currentSelect.name
                            ? theme.palette.colors.primary[350]
                            : theme.palette.colors.primary[200]
                        }
                        key={`cell-${_entry.name}`}
                      />
                    ))}
                    <LabelList dataKey="value" content={<CustomBarLabel />} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )
        ) : (
          <Skeleton variant="rectangular" width="100%" height="100%" />
        )}
      </Box>
    </Box>
  );
};

interface CustomYAxisTickProps {
  data: any;
  Y_TICK_WIDTH: number;
  x?: number;
  y?: number;
  index?: number;
  payload?: any;
}

const CustomYAxisTick = ({ x = 0, y = 0, index = 0, payload, data, Y_TICK_WIDTH }: CustomYAxisTickProps) => (
  <g transform={`translate(${x - Y_TICK_WIDTH}, ${y})`}>
    <foreignObject y={-12} width={Y_TICK_WIDTH} height={24}>
      <Box sx={{ ...flex.row, padding: '0 2px' }}>
        <AvTooltip title={payload.value}>
          <Box
            sx={{
              ...ellipsis,
              flex: 1,
              fontSize: 12,
              textAlign: 'left',
            }}>
            {payload.value}
          </Box>
        </AvTooltip>
        <Box sx={{ flexShrink: 0, ml: '14px', mr: '8px' }}>
          <PercentageChange value={data[index].percentage} />
        </Box>
      </Box>
    </foreignObject>
  </g>
);

interface CustomBarLabelProps {
  x?: number;
  y?: number;
  width?: number;
  value?: string;
}

const CustomBarLabel = ({ x, width, y, value }: CustomBarLabelProps) => (
  <g>
    <text x={+(x || 0) + +(width || 0) + 40} fontSize={12} y={+(y || 0) + 12} fill="var(--color-content-base-primary)" textAnchor="end">
      {value}
    </text>
  </g>
);

const CustomTooltip = (props: any) => {
  const { active, payload, label } = props;
  const theme = useTheme();

  if (active && payload && payload.length) {
    return (
      <WidgetTooltip active={active} payload={payload}>
        <Box
          sx={{
            fontWeight: 600,
            mb: '4px',
            color: 'var(--color-content-immutable-white)',
          }}>
          {label}
        </Box>
        <TooltipDivider />

        <Box sx={{ ...flex.justifyBetweenCenter }}>
          <Box sx={{ color: theme.palette.colors.neutrals[350] }}>Incidents Count</Box>
          <Box sx={{ fontWeight: 600, color: 'var(--color-content-immutable-white)' }}>{payload[0].value.toLocaleString()}</Box>
        </Box>
      </WidgetTooltip>
    );
  }

  return null;
};
export default Top5AttackedEntities;
